export interface Artifact {
  identifier: string;
  type: string;
  title: string;
  content: string;
  version?: string; // Version of the artifact (e.g., "1", "2", "3")
  raw?: string;
  isProcessing?: boolean; // Flag to indicate artifact is still streaming
  readonly_sharable_link_enabled?: boolean;
}

/**
 * Format artifact title with version
 * @param title - The artifact title
 * @param version - The artifact version (optional)
 * @returns Formatted title with version (e.g., "Title - V2")
 */
export function formatArtifactTitle(title: string, version?: string): string {
  if (version) {
    return `${title} - V${version}`;
  }
  return title;
}

export function parseArtifacts(message: string, isStreaming: boolean = false): { parts: (string | Artifact)[] } {
  // Support <artifact>, <antArtifact>, <boltArtifact>
  const regex = /<(artifact|antArtifact|boltArtifact)\s+([^>]+)>([\s\S]*?)<\/(artifact|antArtifact|boltArtifact)>/g;
  let lastIndex = 0;
  let match;
  const parts: (string | Artifact)[] = [];

  while ((match = regex.exec(message)) !== null) {
    if (match.index > lastIndex) {
      parts.push(message.slice(lastIndex, match.index));
    }
    const attrs: Record<string, string> = {};
    const attrMatches = match[2].match(/(\w+)="([^"]*)"/g);
    if (attrMatches) {
      attrMatches.forEach(attr => {
        const [key, value] = attr.split('=');
        if (key && value) {
          attrs[key] = value.replace(/"/g, '');
        }
      });
    }
    // Use 'id' attribute as identifier if present
    const identifier = attrs.id || attrs.identifier || '';
    parts.push({
      identifier,
      type: attrs.type,
      title: attrs.title,
      version: attrs.version, // Extract version attribute
      content: match[3].trim(),
      raw: match[0],
    });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < message.length) {
    parts.push(message.slice(lastIndex));
  }

  // Handle streaming content differently based on streaming state
  const streamingParts: (string | Artifact)[] = [];
  for (const part of parts) {
    if (typeof part === 'string') {
      // Check if this string contains an incomplete artifact tag (streaming scenario)
      const incompleteArtifactRegex = /<(artifact|antArtifact|boltArtifact)\s+([^>]+)>([\s\S]*?)$/;
      const incompleteMatch = part.match(incompleteArtifactRegex);

      if (incompleteMatch) {
        const content = incompleteMatch[3] || '';

        if (isStreaming) {
          // During streaming, create a "processing" artifact to show loading state
          const attrs: Record<string, string> = {};
          const attrMatches = incompleteMatch[2].match(/(\w+)="([^"]*)"/g);
          if (attrMatches) {
            attrMatches.forEach(attr => {
              const [key, value] = attr.split('=');
              if (key && value) {
                attrs[key] = value.replace(/"/g, '');
              }
            });
          }

          const identifier = attrs.id || attrs.identifier || '';
          const processingArtifact: Artifact = {
            identifier,
            type: attrs.type || 'text/html',
            title: attrs.title || 'Generating Artifact...',
            version: attrs.version, // Extract version attribute
            content: content,
            raw: incompleteMatch[0],
            isProcessing: true, // Mark as processing
          };

          streamingParts.push(processingArtifact);
        } else {
          // Not streaming - only create artifact if there's substantial content
          const hasSubstantialContent = content.trim().length > 50;

          if (hasSubstantialContent) {
            // This is a streaming artifact with substantial content
            const attrs: Record<string, string> = {};
            const attrMatches = incompleteMatch[2].match(/(\w+)="([^"]*)"/g);
            if (attrMatches) {
              attrMatches.forEach(attr => {
                const [key, value] = attr.split('=');
                if (key && value) {
                  attrs[key] = value.replace(/"/g, '');
                }
              });
            }

            // Create a streaming artifact with partial content
            const identifier = attrs.id || attrs.identifier || '';
            const streamingArtifact: Artifact = {
              identifier,
              type: attrs.type || 'text/html',
              title: attrs.title || 'Streaming Content',
              version: attrs.version, // Extract version attribute
              content: content,
              raw: incompleteMatch[0],
            };

            streamingParts.push(streamingArtifact);
          } else {
            // Not enough content yet - wrap in markdown code block for proper display
            const wrappedContent = `\`\`\`html\n${content}\n\`\`\``;
            streamingParts.push(wrappedContent);
          }
        }
      } else {
        // Regular string content
        streamingParts.push(part);
      }
    } else {
      // Already processed artifact
      streamingParts.push(part);
    }
  }

  // Now, for each string part, scan for HTML code blocks and convert to artifacts
  const htmlCodeBlockRegex = /```html\s*([\s\S]*?)```/gi;
  const newParts: (string | Artifact)[] = [];
  for (const part of streamingParts) {
    if (typeof part === 'string') {
      let str = part;
      let lastIdx = 0;
      let m;
      while ((m = htmlCodeBlockRegex.exec(str)) !== null) {
        // Only treat as artifact if it looks like a full HTML doc
        const code = m[1].trim();
        if (/<!DOCTYPE html[\s\S]*<\/html>/i.test(code) || /<html[\s\S]*<\/html>/i.test(code)) {
          if (m.index > lastIdx) {
            newParts.push(str.slice(lastIdx, m.index));
          }
          newParts.push({
            identifier: '',
            type: 'text/html',
            title: 'HTML Document',
            content: code,
          });
          lastIdx = htmlCodeBlockRegex.lastIndex;
        }
      }
      if (lastIdx < str.length) {
        newParts.push(str.slice(lastIdx));
      }
    } else {
      newParts.push(part);
    }
  }

  return { parts: newParts };
} 